/* prettier-ignore */

import { inject, Injectable, signal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ToastrService } from "ngx-toastr";
import { AddItemComponent } from './components/add-item/add-item.component';
import { AddCustomLocationComponent } from './components/add-custom-location/add-custom-location.component';
import { BehaviorSubject, catchError, combineLatest, EMPTY, filter, map, take, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ToolsService } from 'src/app/services/tools.service';
import { TranslateService } from '@ngx-translate/core';
import { RestService } from 'src/app/services/rest.service';
import { Router } from '@angular/router';
import { DebugService } from 'src/app/services/debug.service';
import { StatisticsService } from 'src/app/services/statistics.service';
import { NewDetailsPage } from '../new-details/new-details.page';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import Swal from 'sweetalert2';


dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

@Injectable({providedIn: 'root'})

export class NewCustomRouteService {
  private debugService = inject(DebugService);
  
  private readonly MAX_SEARCH_DISTANCE = 15000;
  private readonly CARD_DURATION = 60;

  private readonly DEFAULT_RANGE_TIME: any = {
    startHour: dayjs('09:00', 'HH:mm'),
    endHour: dayjs('00:00', 'HH:mm').add(1, 'day'), 
  }
  private readonly LUNCH_TIME: any = {
    startHour: dayjs('14:00', 'HH:mm'),
    endHour: dayjs('16:00', 'HH:mm')
  }
  private readonly DINNER_TIME: any = {
    startHour: dayjs('21:00', 'HH:mm'),
    endHour: dayjs('22:00', 'HH:mm')
  }

  private statsService = inject(StatisticsService);
  private tools = inject(ToolsService);
  private http = inject(HttpClient);
  private modalController = inject(ModalController);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService)
  private rest = inject(RestService)
  private router = inject(Router);

  private routeItemsSubject = new BehaviorSubject<any>(null);
  public routeItems$ = this.routeItemsSubject.asObservable();
  private registerOptionsSubject = new BehaviorSubject<any>(null);
  public registerOptions$ = this.registerOptionsSubject.asObservable();

  private modalInfoPromise: Promise<void> = Promise.resolve();
  private modalAddItemPromise: Promise<void> = Promise.resolve();
  private modalStartEndPromise: Promise<void> = Promise.resolve();

  public showTimeline = signal(false);
  public route = signal<any>(null);

  public reloadMapEffect = signal(false);
  public selectedDay = signal(0);
  public modal: any;

  public itemToAdd = signal(null);
  public previousLocation = signal(null);

  public allItems: any;
  public filteredItems: any;

  public disableTouch = signal(false);
  public isRecommendedRoute = signal(null);
  public routeConfig: any;

  private potencialFoodCategories: any = []
  private foodTimes: any;

  public techSelectedPoints = signal([]);
  public techSelectedEvents = signal([]);

  private initializedRegisterData = signal(false);
  private initializedAppData = signal(false);

  // constructor(){
  //   this.initializeData();
  // }

  public initializeData() {
    this.tools.enabledInstallBanner.update(prev => false);
    if (this.initializedRegisterData() && this.initializedAppData()){
      this.debugService.info('Loading register and route elements cache data...', '[NewCustomRouteService]');
      return;
    }

    combineLatest([this.tools.isApp$, this.tools.isDistributor$])
      .pipe(filter(([isApp, isDistributor]) => isApp !== null || isDistributor !== null), take(1))
      .subscribe(() => {
        if (!this.initializedRegisterData())
          this.loadRegisterData();

        if (!this.initializedAppData())
          this.loadAppData();
      }
    );
  }

  private loadRegisterData(){
    const url = this.tools.isApp
      ? this.statsService.token 
        ? `${this.rest.apiUrlBase}/api/get-register-app/${this.rest.subDomain}/${this.statsService.token}` 
        : `${this.rest.apiUrlBase}/api/get-register-app/${this.rest.subDomain}`
      : `${this.rest.apiUrlBase}/api/get-register-community-app/${this.rest.subDomain}`

    this.http.get(url)
      .pipe(
        catchError((error) => {
          this.router.navigateByUrl("not-found");
          return throwError(() => new Error('HTTP request failed', { cause: error }));
        }),
        map((response) => {
          // this.rest.replaceUrls(response);
          this.rest.addThumbnail(response, "w200");
          return response;
        })
      ).subscribe((routeItems: any) => {
        this.potencialFoodCategories = routeItems.categories?.filter(
          category => category.name?.['es']?.toLowerCase()?.trim()?.includes('restaur') || category.name?.['es']?.toLowerCase()?.trim()?.includes('afterwork')
        )?.map(category => category.id)?.flat();

        this.debugService.warn(this.potencialFoodCategories, '[NewCustomRouteService] potencialFoodCategories');
        this.registerOptionsSubject.next(routeItems);
        this.initializedRegisterData.set(true);
      }
    );
  }

  private loadAppData() {
    const url = this.tools.isApp
      ? `${this.rest.apiUrlBase}/api/get-data-app/${this.rest.subDomain}`
      : `${this.rest.apiUrlBase}/api/get-data-community-app/${this.rest.subDomain}`

    this.http.get(url)
      .pipe(
        catchError((error) => {
          this.router.navigateByUrl("not-found");
          return throwError(() => new Error('HTTP request failed', { cause: error }));
        }),
        map((response) => {
          // this.tools.replaceUrls(response);
          this.rest.addThumbnail(response, "w500");
          return response;
        })
      ).subscribe((routeOptions: any) => {
        this.routeItemsSubject.next(routeOptions);
        this.initializedAppData.set(true);
      }
    );
  }

  public resetRoute(){
    this.showTimeline.set(false);
    this.selectedDay.set(0);
    this.route.set(null);
    this.routeConfig = {
      startLocation: null,
      startDate: dayjs(),
      endDate: dayjs(),
      startHourFirstDay: this.DEFAULT_RANGE_TIME.startHour, 
      endHourLastDay:    this.DEFAULT_RANGE_TIME.endHour,
      travellerType: null,
      categories:  null,
      subCategories: null
    }
  }

  reloadMap(){
    this.reloadMapEffect.update((prev) => !prev);
  }

  setDay(day: number){
    this.selectedDay.update(prev => day);
  }

  removeItem(itemToRemove){
    const index = this.route().findIndex((route) => route.points.some((item) => item.point?.id === itemToRemove.id && item.point?.type === itemToRemove.type));
    if(index === -1)
      return;
  
    const updatedRoute = this.route().map((route) => ({
      ...route,
      points: route.points.filter((item) => !(item.point?.id === itemToRemove.id && item.point?.type === itemToRemove.type)),
    }));

    this.route.set(updatedRoute);
    this.checkRightTimes();

    if (this.selectedDay() === index){
      this.reloadMap();
      return;
    }

    this.selectedDay.set(index)
    this.setDay(index)
    // this.reloadMap();
  }

  cancelItemToAdd(){
    this.itemToAdd.set(null);
    this.previousLocation.set(null)
    this.reloadMap();
  }

  moveItemInSameDay(day){
    let startTime = this.DEFAULT_RANGE_TIME.startHour.clone();
    this.route()[day].points = this.route()[day].points.map(item => {
        const duration = this.getDuration(item?.point?.duration);
        const endTime = item.type === 'add' 
          ? startTime.clone().add(this.CARD_DURATION, 'minute')
          : startTime.clone().add(duration, 'minute');
        item = {
          ...item, 
          start: startTime.format('HH:mm'), 
          end: endTime.format('HH:mm')
        }
        startTime = endTime.clone();
        return item;
    })
  }

  getAvailableItems(items) {
    const insertedItems = this.route()
      .flatMap(day => day.points)
      .filter(point => point.point?.id)
      .map(point => ({ id: point.point.id, type: point.point.type }));
    return items?.filter(point => !insertedItems.some(inserted => inserted.id === point.id && inserted.type === point.type)
    );
  }

  getPreviousPointLocation(dayIndex, pointIndex){
    for (let i = pointIndex; i >= 0; i--){
      const foundItem = this.route()?.[dayIndex]?.points?.[i - 1]?.point;
      if (foundItem)
        return {
            latitude: parseFloat(foundItem.latitude), 
            longitude: parseFloat(foundItem.longitude)
        };
    }
    return this.routeConfig.startLocation;
  }

  checkItemToAdd(item){
    const itemSize =  Math.ceil((item.item.duration || this.CARD_DURATION) / this.CARD_DURATION);
    if (!this.canBeAdded(item.dayIndex, item.pointIndex, itemSize)){
      Swal.fire({
        title: '',
        text: 'No se ha podido añadir el item al itinerario',
        icon: 'error',
        showCancelButton: false,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      })
      return;
    };
    this.itemToAdd.set(item);
  }

  addItem(){
    if (!this.itemToAdd()) 
      return;

    const item = this.itemToAdd();
    const itemDuration = this.getDuration(item.item?.duration)
    item.item.size =  Math.ceil((item.item?.duration || this.CARD_DURATION) / this.CARD_DURATION);
    this.route()[item.dayIndex].points[item.pointIndex] = {
      ...this.route()[item.dayIndex].points[item.pointIndex],
      type: 'point',
      point: item.item,
      end:  dayjs(this.route()[item.dayIndex].points[item.pointIndex].start, 'HH:mm').clone().add(itemDuration, 'minute').format('HH:mm')
    }

    this.checkRightTimes();
    this.itemToAdd.set(null);
    this.previousLocation.set(null);
    if (this.selectedDay() === item.dayIndex){
      this.reloadMap();
      return;
    }

    this.setDay(item.dayIndex)
  }

  private canBeAdded(dayIndex: number, pointIndex: number, itemSize: number): boolean {
    return Array.from({ length: itemSize }).every((_, index) => this.route()[dayIndex].points[pointIndex + index]?.type === 'add');
  }

  moveItem(listId, itemId, item){
    let startTime = this.route()[parseInt(listId)].points[parseInt(itemId)].start;
    const itemDuration = this.getDuration(item.duration)
    this.route()[parseInt(listId)].points[parseInt(itemId)] = {
      ...item,
      start: startTime, 
      end:  dayjs(startTime, 'HH:mm').clone().add(itemDuration, 'minute').format('HH:mm')
    }
    this.checkRightTimes();
  }

  generateRoute() {
    const route = this.initializeRoute();
    let date = this.routeConfig.startDate.clone();
    route.forEach((day, dayIndex) => {
      let initialHour = dayIndex === 0 
        ? this.routeConfig.startHourFirstDay.clone()
        : this.DEFAULT_RANGE_TIME.startHour.clone();
      let location = this.routeConfig.startLocation;
      this.foodTimes = [...this.foodTimes, { lunch: false, dinner: false }]
      this.processDayPoints(date, initialHour, location, route[dayIndex], dayIndex);
      date = date.add(1, 'day');
    });
    this.debugService.warn(this.foodTimes, '[NewCustomRouteService] foodTimes');

    this.route.set(route);
    this.checkRightTimes();
  }
  
  private initializeRoute(): any[] {
    this.foodTimes = [];
    const totalDays = this.routeConfig.endDate.diff(this.routeConfig.startDate, 'day') + 1;
    return Array(totalDays).fill(null).map((_, index) => ({
      startPoint: this.routeConfig.startLocation,
      startPointName: this.routeConfig.startPointName,
      endPoint: null,
      endPointName: null,
      lowerHour: this.routeConfig.startHourFirstDay.format('HH:mm'),
      upperHour: this.routeConfig.endHourLastDay.format('HH:mm'),
      day: index + 1,
      date: this.routeConfig.startDate.clone().add(index, 'day').format('YYYY/MM/DD'),
      startDate: this.routeConfig.startDate.format('YYYY/MM/DD'),
      endDate: this.routeConfig.endDate.format('YYYY/MM/DD'),
      points: [],
    }));
  }
  
  public processDayPoints(date: Dayjs, initialHour: Dayjs, location: any, routeDay: any, indexDay) {
    while (initialHour.isBefore(this.DEFAULT_RANGE_TIME.endHour.add(1, 'hour'))) {
      const point = this.findNextPoint(initialHour, location, date, indexDay);
      if (point) {
        this.addPointToDay(routeDay, point, initialHour);
        location = { latitude: parseFloat(point.latitude), longitude: parseFloat(point.longitude) };
        const duration = this.getDuration(point.duration);
        const isMultiple = point.duration % this.CARD_DURATION === 0;
        // initialHour = initialHour.add(isMultiple ? duration + this.CARD_DURATION : duration, 'minute');
        initialHour = initialHour.add(duration, 'minute');
      } 
      else 
        initialHour = initialHour.add(this.CARD_DURATION, 'minute');
    }
  }

  private getDuration(minutes) {
    return Math.ceil(minutes / this.CARD_DURATION) * this.CARD_DURATION;
  }

  private isLunchTime(hour){
    return hour.isBetween(this.LUNCH_TIME.startHour, this.LUNCH_TIME.endHour, 'minute', '[)');
  }

  private isDinnerTime(hour){
    return hour.isBetween(this.DINNER_TIME.startHour, this.DINNER_TIME.endHour, 'minute', '[)');
  }

  private isBeetweenLunchTime(startItem, endItem) {  
    return startItem.isBefore(this.LUNCH_TIME.endHour) && endItem.isAfter(this.LUNCH_TIME.startHour);
  }

  private isBeetweenDinnerTime(startItem, endItem){
    return startItem.isBefore(this.DINNER_TIME.endHour) && endItem.isAfter(this.DINNER_TIME.startHour);
  }

  private filterPotencialFood(arrayItems, initialHour){
    if ((this.isLunchTime(initialHour) || this.isDinnerTime(initialHour)) && this.routeConfig.categories?.some(routeCategory => this.potencialFoodCategories?.includes(routeCategory.id)))
      return [arrayItems.filter(item => item.categories?.some(categoryId => this.potencialFoodCategories?.includes(parseInt(categoryId)))), true]

    if ((this.isLunchTime(initialHour) || this.isDinnerTime(initialHour)) && !this.routeConfig.categories?.some(routeCategory => this.potencialFoodCategories?.includes(routeCategory.id)))
      return [arrayItems, false];

    if (this.potencialFoodCategories?.length > 0)
      return [arrayItems.filter(item => !item.categories?.some(categoryId => this.potencialFoodCategories?.includes(parseInt(categoryId)))), false]

    return [arrayItems, false];
  }

  private findNextPoint(initialHour: Dayjs, location: any, date: Dayjs, day: any){
    this.filteredItems = this.sortItemsPerDistance(this.filteredItems, location, this.routeConfig.startLocation, this.MAX_SEARCH_DISTANCE);
    let arrayItems = structuredClone(this.filteredItems)
    let foodItem = false;
    [arrayItems, foodItem] = this.filterPotencialFood(arrayItems, initialHour)
    let point = null;
    while(point === null && arrayItems.length > 0){
      const items = this.getRandomFromFirstN(arrayItems, 4) || [];
      point = this.checkPoints(items, initialHour, date);
    }

    if (this.isLunchTime(initialHour) && this.foodTimes[day].lunch)
      return null;

    if (this.isDinnerTime(initialHour) && this.foodTimes[day].dinner)
      return null;

    if (foodItem){
      if (this.isLunchTime(initialHour))
        this.foodTimes[day] = {...this.foodTimes[day], lunch: true};
      if (this.isDinnerTime(initialHour))
        this.foodTimes[day] = {...this.foodTimes[day], dinner: true};
    }

    return point;
  }

  getRandomFromFirstN(arr: any, size: number) {
      const selectedItems = arr.slice(0, size);
      for (let i = selectedItems.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [selectedItems[i], selectedItems[j]] = [selectedItems[j], selectedItems[i]];
      }
      arr.splice(0, size);
      return selectedItems;
  }
  
  private checkPoints(items: any, initialHour: Dayjs, date: Dayjs): any {
    for (const point of items) {
      if (point.distance > this.MAX_SEARCH_DISTANCE && !initialHour.isSame(this.DEFAULT_RANGE_TIME.startHour, 'minute'))
        continue;

      const isPotencialFoodItem = point.categories.some(categoryId => this.potencialFoodCategories?.includes(parseInt(categoryId)));
      if (!isPotencialFoodItem){
        const pointEndTime = initialHour.clone().add(point.duration, 'minute');        
        // if (this.isBeetweenLunchTime(initialHour, pointEndTime) || this.isBeetweenDinnerTime(initialHour, pointEndTime) || pointEndTime.isAfter(this.DEFAULT_RANGE_TIME.endHour.add(1, 'hour')))
        if (this.isBeetweenLunchTime(initialHour, pointEndTime) || this.isBeetweenDinnerTime(initialHour, pointEndTime) || pointEndTime.isAfter(this.DEFAULT_RANGE_TIME.endHour))
          continue;
      }

      const schedule = this.getScheduleItemPerDay(date, point?.opening_times);
      if (schedule?.time?.some((timeRange: any) => this.getAvailableRangeHour(initialHour, point?.duration, timeRange) === "inRange")) {
        this.filteredItems = this.filteredItems.filter((item: any) => !(item.id === point.id && item.type === point.type));
        point.size = Math.ceil(point?.duration / this.CARD_DURATION);
        return point;
      }
      if (point?.always_open) {
        this.filteredItems = this.filteredItems.filter((item: any) => !(item.id === point.id && item.type === point.type));
        point.size = Math.ceil(point?.duration / this.CARD_DURATION);
        return point;
      }
    }
    return null;
  }

  private addPointToDay(day: any, point: any, initialHour: Dayjs) {
    const itemDuration = this.getDuration(point.duration);
    const item = {
      start: initialHour.format('HH:mm'),
      end: initialHour.clone().add(itemDuration, 'minute').format('HH:mm'),
      point,
      type: "point",
    };
    day.points.push(item);
  }

  checkRightTimes() {
    let errorTimes = false;
    let date = this.routeConfig.startDate.clone();
    const route = this.route().map((day: any) => {
      let defaultInitialHour = this.DEFAULT_RANGE_TIME.startHour.clone();
      const finalHour = this.DEFAULT_RANGE_TIME.endHour.clone();;
      const pointsArray = this.generatePointsArray(day, defaultInitialHour, finalHour, date);
      const isErrorTimes = pointsArray.some(item => item.status === 'ko')
      if (!errorTimes && isErrorTimes) 
        errorTimes = true;

      date = date.add(1, 'day'); 
      return { ...day, points: pointsArray };
    });
    this.route.set(route);
    if (!errorTimes)
      return;

    this.toastr.error(`${this.translate.instant("Hay elementos fuera de horario")}`,``, { timeOut: 3000 });
  }
  
  public generatePointsArray(day: any, defaultInitialHour: Dayjs, finalHour: Dayjs, date: Dayjs): any[] {
    const pointsArray = [];
    while (defaultInitialHour.isSameOrBefore(finalHour)) {
      const hour = defaultInitialHour.format('HH:mm');
      const item = day?.points?.find((item: any) => item.start === hour && item.type !== 'add');
      if (!item) {
        pointsArray.push(this.createEmptySpaceTimeline(hour, defaultInitialHour));
        defaultInitialHour = defaultInitialHour.add(this.CARD_DURATION, 'minute');
      } 
      else {
        pointsArray.push(this.createItemTimeline(item, date, defaultInitialHour));
        const duration = this.getDuration(item.point?.duration);
        defaultInitialHour = defaultInitialHour.add(duration, 'minute');
      }
    }
    return pointsArray;
  }
  
  public createEmptySpaceTimeline(hour: string, defaultInitialHour: Dayjs): any {
    return {
      start: hour,
      end: defaultInitialHour.clone().add(this.CARD_DURATION, 'minute').format('HH:mm'),
      type: 'add',
    };
  }
  
  private createItemTimeline(item: any, date: Dayjs, defaultInitialHour: Dayjs): any {
    const duration = this.getDuration(item.point?.duration);
    const status = this.checkStatus(date, defaultInitialHour, item);
    return {
      ...item,
      start: defaultInitialHour.format('HH:mm'),
      end: defaultInitialHour.clone().add(duration, 'minute').format('HH:mm'),
      type: 'point',
      point: item.point,
      status
    };
  }

  checkStatus(date: Dayjs, hour: Dayjs, item: any): string {
    if (item?.point?.always_open) return 'ok';
    const schedule = this.getScheduleItemPerDay(date, item?.point?.opening_times);
    if (!schedule || schedule?.time?.length === 0) return 'ko';
    const isInAnyRange = schedule.time.some((timeRange: any) => this.getAvailableRangeHour(hour, item?.point?.duration, timeRange) === 'inRange');
    return isInAnyRange ? 'ok' : 'ko';
  }

  getScheduleItemPerDay(currentDay: Dayjs, openingTimes: any[]){
    const weekDays = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    const weekDayString = weekDays[currentDay.day()];
    return openingTimes?.find((e: any) => e.day == weekDayString); 
  }

  private parseTimeRange(timeRange: string): { start: Dayjs, end: Dayjs } {
    const [startTime, endTime] = timeRange?.split(" - ") || [];
    if (!startTime || !endTime) 
      return null;

    const start = dayjs(startTime, "HH:mm");
    const end = dayjs(endTime, "HH:mm").isBefore(start) ? dayjs(endTime, "HH:mm").add(1, "day") : dayjs(endTime, "HH:mm");
    return { start, end };
  }
  
  getAvailableRangeHour(currentHour: Dayjs, duration: number, timeRange: string): string {
    const range = this.parseTimeRange(timeRange);
    if (!range) 
      return "notAvailable";

    const itemEnd = currentHour.clone().add(duration, "minute");
    if (currentHour.isBetween(range.start, range.end, null, "[]") && itemEnd.isBetween(range.start, range.end, null, "[]")) 
      return "inRange";

    return "notAvailable";
  }

//------------------------------------------------------- MODALS --------------------------------------------------------
  async modalItemInfo(item, removeItem = false) {
    this.modalInfoPromise = this.modalInfoPromise.then(async () => {
      if (this.modal) 
        return;

      this.modal = await this.modalController.create({
        component: NewDetailsPage,
        cssClass: "detailsModal",
        componentProps: {
          isModal: true,
          item: item,
          removeItem
        },
      });

      await this.modal.present();
      const { data } = await this.modal.onDidDismiss();
      this.modal = null;
      if (data){
        this.removeItem(item)
      }
    });
    return this.modalInfoPromise;
  }

  async modalAddItem(dayIndex, pointIndex) {
    const prevLocation = this.getPreviousPointLocation(dayIndex, pointIndex);
    const hour = dayjs(this.route()[dayIndex].points[pointIndex].start, 'HH:mm')
    const availablePoints = this.sortItemsPerDistance(this.getAvailableItems(this.allItems), prevLocation, this.routeConfig.startLocation, this.MAX_SEARCH_DISTANCE)?.filter(
      item => this.canBeAdded(dayIndex, pointIndex, Math.ceil((item.duration || this.CARD_DURATION) / this.CARD_DURATION))
    );

    let availableTechItems = [];
    if (this.tools.isTechMode()){
      const techSelectedItems = [
        this.allItems.filter(item => this.techSelectedPoints()?.includes(item.id) && item.type === 'point'),
        this.allItems.filter(item => this.techSelectedEvents()?.includes(item.id) && item.type === 'event'),
      ].flat();
  
      availableTechItems = this.sortItemsPerDistance(this.getAvailableItems(techSelectedItems), prevLocation, this.routeConfig.startLocation, this.MAX_SEARCH_DISTANCE)?.filter(
        item => this.canBeAdded(dayIndex, pointIndex, Math.ceil((item.duration || this.CARD_DURATION) / this.CARD_DURATION))
      );
    }

    this.modalAddItemPromise = this.modalAddItemPromise.then(async () => {
      if (this.modal) 
        return;

      this.modal = await this.modalController.create({
        component: AddItemComponent,
        cssClass: "detailsModal",
        componentProps: {
          availableItems: availablePoints,
          availableTechItems: availableTechItems,
          day: dayIndex,
          hour: hour.format('HH:mm')
        }
      });

      await this.modal.present();
      const { data } = await this.modal.onDidDismiss();
      this.modal = null;
      if (!data) 
        return;

      const location = this.getPreviousPointLocation(dayIndex, pointIndex);
      this.previousLocation.set({lat: location.latitude, lng: location.longitude});
      const item = { dayIndex, pointIndex, item: data};
      this.showTimeline.set(false);
      this.checkItemToAdd(item);
    })
    return this.modalAddItemPromise
  }

  async openModalStartEnd() {
    const routeSize = this.route()?.length;
    const arrayDays = Array.from({ length: routeSize }, (_, i) => i + 1);
    this.modalStartEndPromise = this.modalStartEndPromise.then(async () => {
      if (this.modal) 
        return;

      this.modal = await this.modalController.create({
        component: AddCustomLocationComponent,
        cssClass: "custonLocationModal",
        componentProps: { arrayDays: arrayDays },
      });
  
      await this.modal.present();
      const { data } = await this.modal.onDidDismiss();
      this.modal = null;
  
      if (!data?.type || !data?.day || !data?.location) 
        return;

      if (data.day === 'all'){
        this.route().forEach(day => {
          if (data.type === 'start'){
            day.startPoint = data.location;
            day.startPointName = data.location?.name;
          }
          if (data.type === 'end'){
            day.endPoint = data.location;
            day.endPointName = data.location?.name;
          }
        });
      }
      
      if (data.type === 'start' && data.day !== 'all'){
        this.route()[(data.day - 1)].startPoint = data.location;
        this.route()[(data.day - 1)].startPointName = data.location?.name;
        this.setDay(data.day - 1)
      }
      if (data.type === 'end' && data.day !== 'all'){
        this.route()[(data.day - 1)].endPoint = data.location;
        this.route()[(data.day - 1)].endPointName = data.location?.name;
        this.setDay(data.day - 1)
      }
      this.reloadMap();
    });
    return this.modalStartEndPromise
  }

  toggleTechPoints(id){
    const point = this.techSelectedPoints()?.find(pointId => pointId === id);
    if (!point){
      this.techSelectedPoints.update(prev => [...prev, id]);
      return;
    }
    this.techSelectedPoints.update(prev => prev.filter(pointId => pointId !== id))
  }

  toggleTechEvents(id){
    const point = this.techSelectedPoints()?.find(pointId => pointId === id);
    if (!point){
      this.techSelectedPoints.update(prev => [...prev, id]);
      return;
    }
    this.techSelectedPoints.update(prev => prev.filter(pointId => pointId !== id))
  }

// --------------------------------------------- REGISTER USER ITEMS FILTERS --------------------------------------------
  getApps(){
    return this.routeItemsSubject.getValue()?.filter_apps;
  }

  getCategories(){
    return this.registerOptionsSubject.getValue()?.categories;
  }

  getCountries(){
    return this.registerOptionsSubject.getValue()?.countries;
  }

  getProvinces(){
    return this.registerOptionsSubject.getValue()?.provinces;
  }

  filterEventsByDate(events){
    return events?.filter(event => {
      const dateEventStart = dayjs(event.start, "YYYY-MM-DD");
      const dateEventEnd = dayjs(event.end, "YYYY-MM-DD");
      return (
        this.routeConfig.startDate.isBetween(dateEventStart, dateEventEnd, null, "[]") || this.routeConfig.endDate.isBetween(dateEventStart, dateEventEnd, null, "[]") ||
        dateEventStart.isBetween(this.routeConfig.startDate, this.routeConfig.endDate, null, "[]") || dateEventEnd.isBetween(this.routeConfig.startDate, this.routeConfig.endDate, null, "[]")
      );
    })
  }

  filterTechItems(){
    let { interestPoints, events } = this.routeItemsSubject.getValue();
    events = this.filterEventsByDate(events);
    this.allItems = [...interestPoints, ...events];
    this.allItems = this.allItems?.filter(item => item.duration && item.duration > 0 && item.coordinates && item.coordinates != "" && (item.opening_times?.length > 0 || item.always_open));
    this.filteredItems = [
      this.allItems.filter(item => this.techSelectedPoints()?.includes(item.id) && item.type === 'point'),
      this.allItems.filter(item => this.techSelectedEvents()?.includes(item.id) && item.type === 'event'),
    ].flat();
  }

  filterAppItems(){
    let { interestPoints, events } = this.routeItemsSubject.getValue();
    events = this.filterEventsByDate(events);

    this.allItems = [...interestPoints, ...events];
    this.allItems = this.allItems?.filter(item => item.duration && item.duration > 0 && item.coordinates && item.coordinates != "" && (item.opening_times?.length > 0 || item.always_open));

    this.filteredItems = [];
    this.routeConfig?.categories?.forEach(category => {
      let items = []
      if (!category.subcategories || category.subcategories?.length === 0){
        items = this.allItems.filter(item => 
          (item.type_travelers?.includes(this.routeConfig?.travellerType) || item.type_travelers?.length === 0) && item.categories.includes(`${category.id}`)
        )
      }
      else{
        items = this.allItems.filter(item => 
          (item.type_travelers?.includes(this.routeConfig?.travellerType) || item.type_travelers?.length === 0) && item.categories.includes(`${category.id}`)
          && item.subcategories?.some(sub => category.subcategories?.includes(parseInt(sub)))
        )
      }
      
      if (items.length === 0) 
        return;

      this.filteredItems = [...this.filteredItems, items]?.flat();
    });
  }

  filterDistributorItems(){
    let { interestPoints, events } = this.routeItemsSubject.getValue();
    events = this.filterEventsByDate(events);

    this.allItems = [...interestPoints, ...events];
    this.allItems = this.allItems?.filter(item => item.duration && item.duration > 0 && item.coordinates && item.coordinates != "" && (item.opening_times?.length > 0 || item.always_open));
    // const allItemsByClient = this.getApps()?.map(app => {
    //   const items = this.allItems.filter(item => item.client_id?.includes(`${app.client_id}`));
    //   return { id: app.client_id, items }
    // })

    this.filteredItems = [];
    this.routeConfig.categories?.forEach(category => {
      let items = [];
      if (!category.subcategories || category.subcategories?.length === 0){
        items = this.allItems.filter(item => 
          // (item.type_travelers?.includes(this.routeConfig.travellerType) || item.type_travelers?.length === 0) &&
           item.categories?.some(cat => category.ids?.includes(parseInt(cat)))
        )
      }
      else{
        items = this.allItems.filter(item => 
          // (item.type_travelers?.includes(this.routeConfig.travellerType) || item.type_travelers?.length === 0) && 
          item.categories?.some(cat => category.ids?.includes(parseInt(cat))) && item.subcategories?.some(sub => category.subcategories?.includes(parseInt(sub)))
        )
      }
      if (items.length === 0) 
        return;

      this.filteredItems = [...this.filteredItems, items]?.flat();
    });

    // const filteredItemsByDay = this.routeConfig.distributorSelectedClientByDay?.map(client_id => {
    //   const items = this.filteredItems.filter(item => item.client_id?.includes(`${client_id}`))
    //   return { id: parseInt(client_id), items}
    // })
  }

//-------------------------------------- CARGA RUTAS DIRECTAS ------------------------------------------------------------
  mapRouteStructure(route){
    return route.map((day, indexDay) => {
      if (day.isRefactorRoute){
        if (indexDay == 0){
          this.routeConfig = {
            startLocation: day.startPoint,
            startDate: dayjs(day.startDate, 'YYYY/MM/DD'),
            endDate: dayjs(day.endDate, 'YYYY/MM/DD'),
            startHourFirstDay: dayjs(day.lowerHour, 'HH:mm'),
            endHourLastDay: dayjs(day.upperHour, 'HH:mm'),
            travellerType: null,
            categories:  null,
            subCategories: null
          }
        }
        day.points = day.points.map(item => {
          if (item.type === 'add') return item;
          return {
            ...item,
            point: {
              ...item.point,
              size: Math.ceil(item?.point?.duration / this.CARD_DURATION)
            }
          }
        })?.filter(item => item.type === 'add' || (item.type === 'point' && item?.point?.duration > 0));
        return day;
      }

      if (indexDay == 0){
        this.routeConfig = {
          startLocation: day.startPoint,
          startDate: dayjs(day.startDate, 'YYYY/MM/DD'),
          endDate: dayjs(day.endDate, 'YYYY/MM/DD'),
          startHourFirstDay: dayjs().set('hour', day.lowerHour)?.set('minute', 0),
          endHourLastDay:    dayjs().set('hour', day.upperHour)?.set('minute', 0),
          travellerType: null,
          categories:  null,
          subCategories: null
        }
      }
      
      day.points = day.points.filter(item => (item.type === 'point' && item?.point?.duration > 0)).map(item => ({
        ...item,
        start: dayjs().set('hour', item.start)?.set('minute', 0)?.format('HH:mm'),
        end: dayjs().set('hour', item.start)?.set('minute', item.point.duration)?.format('HH:mm'),
        point: {
          ...item.point,
          size: Math.ceil(item?.point?.duration / this.CARD_DURATION)
        }
      }))

      let defaultInitialHour = this.DEFAULT_RANGE_TIME.startHour.clone();
      const finalHour = this.DEFAULT_RANGE_TIME.endHour.clone();;
      const date = this.routeConfig.startDate.clone().add(indexDay, 'day');
      const populatedPoints = this.generatePointsArray(day, defaultInitialHour, finalHour, date);

      return{
        ...day,
        lowerHour: dayjs().set('hour', day.lowerHour)?.set('minute', 0)?.format('HH:mm'),
        upperHour: dayjs().set('hour', day.upperHour)?.set('minute', 0)?.format('HH:mm'),
        startDate: day.startDate,
        endDate: day.startDate,
        date: dayjs(day.startDate, 'YYYY/MM/DD').clone().add(indexDay, 'day').format('YYYY/MM/DD'),
        points: populatedPoints
      }
    })
  }

  loadRecommendedRoute(){
    this.isRecommendedRoute.set(false);
    const totalDays = this.routeConfig.endDate.diff(this.routeConfig.startDate, 'day') + 1;
    let routeTest =  Array(totalDays).fill(null).map((_, index) => {
      if(this.route()?.[index])
        return {
          ...this.route()[index],
          day: index + 1,
          date: this.routeConfig.startDate.clone().add(index, 'day').format('YYYY/MM/DD'),
          startDate: this.routeConfig.startDate.format('YYYY/MM/DD'),
          endDate: this.routeConfig.endDate.format('YYYY/MM/DD')
        }

      let defaultInitialHour = this.DEFAULT_RANGE_TIME.startHour.clone();
      const finalHour = this.DEFAULT_RANGE_TIME.endHour.clone();;
      let addArray = [];
      while (defaultInitialHour.isSameOrBefore(finalHour)) {
        const hour = defaultInitialHour.format('HH:mm');
        addArray.push(this.createEmptySpaceTimeline(hour, defaultInitialHour));
        defaultInitialHour = defaultInitialHour.add(this.CARD_DURATION, 'minute');
      }

      return{
        startPoint: this.routeConfig.startLocation,
        startPointName: this.routeConfig.startPointName,
        endPoint: null,
        endPointName: null,
        lowerHour: this.routeConfig.startHourFirstDay.format('HH:mm'),
        upperHour: this.routeConfig.endHourLastDay.format('HH:mm'),
        day: index + 1,
        date: this.routeConfig.startDate.clone().add(index, 'day').format('YYYY/MM/DD'),
        startDate: this.routeConfig.startDate.format('YYYY/MM/DD'),
        endDate: this.routeConfig.endDate.format('YYYY/MM/DD'),
        points: addArray
      }
    })

    this.route.set(routeTest);
    this.checkRightTimes();
  }

// --------------------------------------------------- GENERATE SEND STRUCTURE  ---------------------------------------------------
  generateSendStructure(){
    const data = structuredClone(this.route());
    return data?.map((route, index) => ({
        ...route,
        isRefactorRoute: true,
        points: route.points.map((item) => {
          if(item.type === 'add') return item;
          return { ...item, point: { id: item.point.id, type: item.point.type } }
        })
    }))
  }

//------------------------------------------ AUXILIAR DISTANCE FUNCTIONS ------------------------------------------------------------
  sortItemsPerDistance(items, lastLocation, startLocation, maxDistance = 15000) {
    const itemsFromLastLocation = items?.map((item) => {
      item.distance = this.getDistanceBetween(parseFloat(lastLocation.latitude), parseFloat(lastLocation.longitude), parseFloat(item.latitude), parseFloat(item.longitude));
      item.distanceM = item.distance >= 1000 ? (item.distance / 1000).toFixed(2) + " Km's" : item.distance.toFixed(0) + " M's";
      return item;
    }).sort((a, b) => a.distance - b.distance);

    if (itemsFromLastLocation?.length === 0)
      return itemsFromLastLocation;

    if (itemsFromLastLocation?.[0]?.distance > maxDistance){
      return items?.map((item) => {
        item.distance = this.getDistanceBetween(parseFloat(startLocation.latitude), parseFloat(startLocation.longitude), parseFloat(item.latitude), parseFloat(item.longitude));
        item.distanceM = item.distance >= 1000 ? (item.distance / 1000).toFixed(2) + " Km's" : item.distance.toFixed(0) + " M's";
        return item;
      }).sort((a, b) => a.distance - b.distance);
    }

    return itemsFromLastLocation;
  }

  getDistanceBetween(lat1: any, lon1: any, lat2: any, lon2: any): any {
    const rad = function (x) { return (x * Math.PI) / 180 };
    const R = 6378.137;
    const dLat = rad(lat2 - lat1);
    const dLong = rad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +  Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c * 1000;
    return d;
  }

}